import * as React from 'react';
import Box from '@mui/material/Box';
import FallbackAvatar from '../FallbackAvatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Logout from '@mui/icons-material/Logout';
import { AppRoutes } from '../../constants/routes';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useAuthProvider } from "../../utils/auth";
import { useQuery } from 'react-query';
import { fetcher } from '../../utils/api';
import { ApiRoutes, getImageUrl, getPublicProfileRoute } from '../../constants/routes';
import MicExternalOnIcon from '@mui/icons-material/MicExternalOn';
import LibraryMusicIcon from '@mui/icons-material/LibraryMusic';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import MessageIcon from '@mui/icons-material/Message';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CreditStatusIndicator from '../CreditStatusIndicator';
import ThemeSelector from '../ThemeSelector/ThemeSelector';
import InstallAppMenuItem from '../InstallAppMenuItem';


const textStyle = {
    fontFamily: 'Inter',
    marginLeft: 2,
}

const ProfileMenu = () => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [hasUnsavedCredits, setHasUnsavedCredits] = React.useState(false);
    const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = React.useState(false);
    const [isSavingChanges, setIsSavingChanges] = React.useState(false);
    const [pendingNavigation, setPendingNavigation] = React.useState(null);
    const open = Boolean(anchorEl);
    const navigate = useNavigate();
    const { userType, deauthorizeUser, user, isCreatorListeningMode, isActListeningMode, isLabelListeningMode, isPublisherListeningMode, currentActId, exitCreatorListeningMode, exitActListeningMode, enterLabelListeningMode, exitLabelListeningMode, exitPublisherListeningMode, isPodcasterListeningMode, exitPodcasterListeningMode, curatingLabel } = useAuthProvider();
    // Labels this team member may curate for ("Listen & Curate as <label>")
    const { data: curationLabels = [] } = useQuery({
        queryKey: ['curation-labels', user?.uuid],
        queryFn: () => fetcher(ApiRoutes.CurationLabels),
        enabled: userType === 'label_team_member' || !!user?.organizations?.labels?.length,
        staleTime: 5 * 60 * 1000,
    });
    
    // Fetch act data if in act listening mode
    const { data: act } = useQuery({
        queryKey: ['act-detail', currentActId],
        queryFn: () => fetcher(`${ApiRoutes.Act}${currentActId}/`),
        enabled: isActListeningMode && !!currentActId
    });

    // Listen for unsaved credits changes
    React.useEffect(() => {
        const checkUnsavedCredits = () => {
            setHasUnsavedCredits(window.__hasUnsavedRecordingCredits || false);
        };
        
        // Check on mount
        checkUnsavedCredits();
        
        // Listen for changes
        window.addEventListener('unsavedCreditsChanged', checkUnsavedCredits);
        
        return () => {
            window.removeEventListener('unsavedCreditsChanged', checkUnsavedCredits);
        };
    }, []);


    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    // Safe navigation function that checks for unsaved credits
    const safeNavigate = (to, options = {}) => {
        if (hasUnsavedCredits) {
            setPendingNavigation(() => () => navigate(to, options));
            setShowUnsavedChangesDialog(true);
            setAnchorEl(null); // Close the menu
        } else {
            navigate(to, options);
        }
    };

    const doLogout = (e) => {
        // Check for unsaved changes before logging out
        if (hasUnsavedCredits) {
            setPendingNavigation(null); // No navigation, just logout
            setShowUnsavedChangesDialog(true);
            return;
        }
        
        deauthorizeUser().then(() => {
          navigate("/");
        });
    }
    
    const handleUnsavedChangesDialogClose = async (action) => {
        if (action === 'save') {
            // Save changes and then proceed with pending action
            setIsSavingChanges(true);
            try {
                if (window.__saveRecordingCredits) {
                    const result = await window.__saveRecordingCredits();
                    if (result.success) {
                        // Clear the unsaved changes flag
                        window.__hasUnsavedRecordingCredits = false;
                        setHasUnsavedCredits(false);
                        setShowUnsavedChangesDialog(false);
                        setIsSavingChanges(false);
                        
                        // Execute pending navigation or logout
                        if (pendingNavigation) {
                            pendingNavigation();
                            setPendingNavigation(null);
                        } else {
                            // If no pending navigation, it was a logout
                            deauthorizeUser().then(() => {
                                navigate("/");
                            });
                        }
                    } else {
                        setIsSavingChanges(false);
                        alert(`Failed to save changes: ${result.message || 'Unknown error'}`);
                    }
                } else {
                    setIsSavingChanges(false);
                    alert('Unable to save changes. Please go back to the page and save manually.');
                }
            } catch (error) {
                setIsSavingChanges(false);
                console.error('Error saving changes:', error);
                alert(`Failed to save changes: ${error.message || 'Unknown error'}`);
            }
            return;
        }
        
        if (action === 'discard') {
            // Clear the unsaved changes flag and proceed with pending action
            window.__hasUnsavedRecordingCredits = false;
            setHasUnsavedCredits(false);
            setShowUnsavedChangesDialog(false);
            
            // Execute pending navigation or logout
            if (pendingNavigation) {
                pendingNavigation();
                setPendingNavigation(null);
            } else {
                // If no pending navigation, it was a logout
                deauthorizeUser().then(() => {
                    navigate("/");
                });
            }
        } else if (action === 'cancel') {
            setShowUnsavedChangesDialog(false);
            setPendingNavigation(null);
        }
    }


    return (
        <React.Fragment>
            <Box sx={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
                <Tooltip title="Account">
                <IconButton
                    onClick={handleClick}
                    size="small"
                    sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%'
                    }}
                    aria-controls={open ? 'account-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                >
                    <FallbackAvatar 
                        src={isActListeningMode && act?.profile_image ? getImageUrl(act.profile_image) : user?.profile_picture}
                        size={32}
                        user={isActListeningMode && act ? { name: act.name } : user}
                        showPersonIcon={false}
                    />
                </IconButton>
                </Tooltip>
            </Box>
            <Menu
                anchorEl={anchorEl}
                id="account-menu"
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                PaperProps={{
                elevation: 0,
                sx: {
                    overflow: 'visible',
                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                    mt: 1.5,
                    '& .MuiAvatar-root': {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                    },
                    '&::before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                    },
                },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                {/* Credit Balance Display - only for listeners and users in listening mode */}
                {(userType === 'listener' || (userType === 'creator' && (isCreatorListeningMode || isActListeningMode)) || (userType === 'label' && isLabelListeningMode) || (userType === 'label_team_member' && isLabelListeningMode) || (userType === 'publisher' && isPublisherListeningMode) || (userType === 'podcaster' && isPodcasterListeningMode)) && (
                  <MenuItem disabled sx={{ opacity: 1, cursor: 'default' }}>
                      <CreditStatusIndicator showIcon={true} showLabel={true} size="small" />
                  </MenuItem>
                )}
                
                {/* Creator Dashboard Option for creators in listening mode */}
                {userType === 'creator' && isCreatorListeningMode && (
                  <MenuItem 
                    onClick={() => {
                      exitCreatorListeningMode();
                      safeNavigate(AppRoutes.CreatorDash);
                    }}
                  >
                    <MicExternalOnIcon/>
                    <Typography variant="body2" sx={textStyle}>
                      Creator Dashboard
                    </Typography>
                  </MenuItem>
                )}

                {/* Act Dashboard Option for creators in act listening mode */}
                {userType === 'creator' && isActListeningMode && currentActId && (
                  <MenuItem 
                    onClick={() => {
                      exitActListeningMode();
                      safeNavigate(`/creator/acts/${currentActId}/dashboard`);
                    }}
                  >
                    <MicExternalOnIcon/>
                    <Typography variant="body2" sx={textStyle}>
                      Artist Dashboard
                    </Typography>
                  </MenuItem>
                )}

                {/* Label Dashboard Option for labels in listening mode */}
                {userType === 'label' && isLabelListeningMode && (
                  <MenuItem 
                    onClick={() => {
                      exitLabelListeningMode();
                      safeNavigate(AppRoutes.LabelDash);
                    }}
                  >
                    <MicExternalOnIcon/>
                    <Typography variant="body2" sx={textStyle}>
                      Label Dashboard
                    </Typography>
                  </MenuItem>
                )}

                {/* Podcaster Dashboard Option for podcasters in listening mode */}
                {userType === 'podcaster' && isPodcasterListeningMode && (
                  <MenuItem 
                    onClick={() => {
                      exitPodcasterListeningMode();
                      safeNavigate(AppRoutes.PodcasterDash);
                    }}
                  >
                    <MicExternalOnIcon/>
                    <Typography variant="body2" sx={textStyle}>
                      Podcaster Dashboard
                    </Typography>
                  </MenuItem>
                )}

                {/* Who a label team member's playlists are being curated for */}
                {userType === 'label_team_member' && isLabelListeningMode && (
                  <MenuItem disabled sx={{ opacity: 1, cursor: 'default' }}>
                    <Typography variant="caption" color="text.secondary" sx={textStyle}>
                      Curating as {curatingLabel ? curatingLabel.name : 'yourself'}
                    </Typography>
                  </MenuItem>
                )}

                {/* Label Dashboard Option for label team members in listening mode */}
                {userType === 'label_team_member' && isLabelListeningMode && (
                  <MenuItem 
                    onClick={() => {
                      exitLabelListeningMode();
                      safeNavigate(AppRoutes.LabelDash);
                    }}
                  >
                    <MicExternalOnIcon/>
                    <Typography variant="body2" sx={textStyle}>
                      Label Dashboard
                    </Typography>
                  </MenuItem>
                )}

                {/* Listening-mode items for label team members */}
                {userType === 'label_team_member' && isLabelListeningMode && (
                  <>
                    <MenuItem 
                    component={RouterLink}
                    to={getPublicProfileRoute(user)}>
                        <AccountCircleIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Public Profile
                        </Typography>
                    </MenuItem>

                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.ListenerPayments}>
                        <CreditCardIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Payment
                        </Typography>
                    </MenuItem>

                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.ListenerCurationSettings}>
                        <LibraryMusicIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Curation
                        </Typography>
                    </MenuItem>

                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.Messages}>
                        <MessageIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Messages
                        </Typography>
                    </MenuItem>
                  </>
                )}

                {/* Listener menu items */}
                {userType === 'listener' && (
                  <>
                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.ListenerProfile}
                    >
                        <AccountCircleIcon/>
                        <Typography variant="body2" sx={textStyle}>
                        Public Profile
                        </Typography>
                    </MenuItem>

                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.ListenerAccountSettings}>
                        <SettingsIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Account Settings
                        </Typography>
                    </MenuItem>

                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.ListenerPayments}>
                        <CreditCardIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Payment
                        </Typography>
                    </MenuItem>

                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.ListenerCurationSettings}>
                        <LibraryMusicIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Curation
                        </Typography>
                    </MenuItem>

                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.Messages}>
                        <MessageIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Messages
                        </Typography>
                    </MenuItem>
                  </>
                )}

                {/* Creator in listening mode menu items */}
                {userType === 'creator' && (isCreatorListeningMode || isActListeningMode) && (
                  <>
                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.ListenerProfile}
                    >
                        <AccountCircleIcon/>
                        <Typography variant="body2" sx={textStyle}>
                        Public Profile
                        </Typography>
                    </MenuItem>

                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.ListenerAccountSettings}>
                        <SettingsIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Account Settings
                        </Typography>
                    </MenuItem>

                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.ListenerPayments}>
                        <CreditCardIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Payment
                        </Typography>
                    </MenuItem>

                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.ListenerCurationSettings}>
                        <LibraryMusicIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Curation
                        </Typography>
                    </MenuItem>

                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.Messages}>
                        <MessageIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Messages
                        </Typography>
                    </MenuItem>
                  </>
                )}

                {/* Label in listening mode menu items */}
                {userType === 'label' && isLabelListeningMode && (
                  <>
                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.LabelProfile}
                    >
                        <AccountCircleIcon/>
                        <Typography variant="body2" sx={textStyle}>
                        Public Profile
                        </Typography>
                    </MenuItem>

                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.LabelAccountSettings}>
                        <SettingsIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Account Settings
                        </Typography>
                    </MenuItem>

                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.ListenerPayments}>
                        <CreditCardIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Payment
                        </Typography>
                    </MenuItem>

                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.ListenerCurationSettings}>
                        <LibraryMusicIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Curation
                        </Typography>
                    </MenuItem>

                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.Messages}>
                        <MessageIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Messages
                        </Typography>
                    </MenuItem>
                  </>
                )}

                {/* Podcaster menu items */}
                {userType === 'podcaster' && isPodcasterListeningMode && (
                  <>
                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.PodcasterProfile}>
                        <AccountCircleIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Public Profile
                        </Typography>
                    </MenuItem>

                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.ListenerPayments}>
                        <CreditCardIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Payment
                        </Typography>
                    </MenuItem>

                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.ListenerCurationSettings}>
                        <LibraryMusicIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Curation
                        </Typography>
                    </MenuItem>

                    <MenuItem 
                    component={RouterLink}
                    to="/podcaster/messages">
                        <MessageIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Messages
                        </Typography>
                    </MenuItem>
                  </>
                )}

                {userType === 'podcaster' && !isPodcasterListeningMode && (
                  <>
                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.PodcasterDash}>
                        <MicExternalOnIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Podcaster Dashboard
                        </Typography>
                    </MenuItem>

                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.PodcasterAccountSettings}>
                        <SettingsIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Account Settings
                        </Typography>
                    </MenuItem>

                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.PodcasterProfile}>
                        <AccountCircleIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Public Profile
                        </Typography>
                    </MenuItem>

                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.PodcasterListeningPortal}>
                        <HeadphonesIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Listen & Curate
                        </Typography>
                    </MenuItem>

                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.ListenerCurationSettings}>
                        <LibraryMusicIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Curation
                        </Typography>
                    </MenuItem>

                    <MenuItem 
                    component={RouterLink}
                    to="/podcaster/messages">
                        <MessageIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Messages
                        </Typography>
                    </MenuItem>
                  </>
                )}

                {/* Creator not in listening mode menu items */}
                {userType === 'creator' && !isCreatorListeningMode && !isActListeningMode && (
                  <>
                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.CreatorDash}>
                        <MicExternalOnIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Creator Dashboard
                        </Typography>
                    </MenuItem>

                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.CreatorAccountSettings}>
                        <SettingsIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Account Settings
                        </Typography>
                    </MenuItem>

                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.CreatorProfile}>
                        <AccountCircleIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Public Profile
                        </Typography>
                    </MenuItem>

                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.CreatorListeningPortal}>
                        <HeadphonesIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Listen & Curate
                        </Typography>
                    </MenuItem>

                    <MenuItem 
                    component={RouterLink}
                    to="/creator/messages">
                        <MessageIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Messages
                        </Typography>
                    </MenuItem>

                  </>
                )}

                {/* Label-specific menu items */}
                {userType === 'label' && !isLabelListeningMode && (
                  <>
                    {/* Label Dashboard */}
                    <MenuItem 
                      onClick={() => {
                        safeNavigate(AppRoutes.LabelDash);
                      }}
                    >
                      <MicExternalOnIcon/>
                      <Typography variant="body2" sx={textStyle}>
                        Label Dashboard
                      </Typography>
                    </MenuItem>

                    {/* Messages */}
                    <MenuItem 
                    component={RouterLink}
                    to="/label/messages">
                        <MessageIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Messages
                        </Typography>
                    </MenuItem>

                    {/* Account Settings */}
                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.LabelAccountSettings}>
                        <SettingsIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Account Settings
                        </Typography>
                    </MenuItem>

                    {/* Public Profile */}
                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.LabelProfile}>
                        <AccountCircleIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Public Profile
                        </Typography>
                    </MenuItem>

                    {/* Enter listening mode for labels */}
                    <MenuItem 
                      onClick={() => {
                        enterLabelListeningMode();
                        safeNavigate(AppRoutes.Home2);
                      }}
                    >
                      <HeadphonesIcon/>
                      <Typography variant="body2" sx={textStyle}>
                        Listen & Curate
                      </Typography>
                    </MenuItem>
                  </>
                )}

                {/* Label Team Member-specific menu items */}
                {userType === 'label_team_member' && !isLabelListeningMode && (
                  <>
                    {/* Label Dashboard */}
                    <MenuItem 
                      onClick={() => {
                        safeNavigate(AppRoutes.LabelDash);
                      }}
                    >
                      <MicExternalOnIcon/>
                      <Typography variant="body2" sx={textStyle}>
                        Label Dashboard
                      </Typography>
                    </MenuItem>

                    {/* Messages */}
                    <MenuItem 
                    component={RouterLink}
                    to="/label/messages">
                        <MessageIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Messages
                        </Typography>
                    </MenuItem>

                    {/* Account Settings */}
                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.LabelAccountSettings}>
                        <SettingsIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Account Settings
                        </Typography>
                    </MenuItem>

                    {/* Public Profile */}
                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.LabelProfile}>
                        <AccountCircleIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Public Profile
                        </Typography>
                    </MenuItem>

                    {/* Enter listening mode for label team members: as themselves,
                        or on behalf of each label their role lets them curate for */}
                    <MenuItem 
                      onClick={() => {
                        enterLabelListeningMode(null);
                        safeNavigate(AppRoutes.Home2);
                      }}
                    >
                      <HeadphonesIcon/>
                      <Typography variant="body2" sx={textStyle}>
                        Listen & Curate as Me
                      </Typography>
                    </MenuItem>
                    {curationLabels.map((label) => (
                      <MenuItem 
                        key={label.id}
                        onClick={() => {
                          enterLabelListeningMode({ id: label.id, name: label.name });
                          safeNavigate(AppRoutes.Home2);
                        }}
                      >
                        <HeadphonesIcon/>
                        <Typography variant="body2" sx={textStyle}>
                          Listen & Curate as {label.name}
                        </Typography>
                      </MenuItem>
                    ))}
                  </>
                )}

                {/* Publisher-specific menu items */}
                {userType === 'publisher' && !isPublisherListeningMode && (
                  <>
                    {/* Publisher Dashboard */}
                    <MenuItem 
                      onClick={() => {
                        safeNavigate(AppRoutes.PublisherDash);
                      }}
                    >
                      <MicExternalOnIcon/>
                      <Typography variant="body2" sx={textStyle}>
                        Publisher Dashboard
                      </Typography>
                    </MenuItem>

                    {/* Messages */}
                    <MenuItem 
                    component={RouterLink}
                    to="/publisher/messages">
                        <MessageIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Messages
                        </Typography>
                    </MenuItem>

                    {/* Account Settings */}
                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.PublisherAccountSettings}>
                        <SettingsIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Account Settings
                        </Typography>
                    </MenuItem>

                    {/* Public Profile */}
                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.PublisherProfile}>
                        <AccountCircleIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Public Profile
                        </Typography>
                    </MenuItem>

                    {/* Enter listening mode for publishers */}
                    <MenuItem 
                      component={RouterLink}
                      to={AppRoutes.PublisherListeningPortal}
                    >
                      <HeadphonesIcon/>
                      <Typography variant="body2" sx={textStyle}>
                        Listen & Curate
                      </Typography>
                    </MenuItem>
                  </>
                )}

                {/* Publisher Dashboard Option for publishers in listening mode */}
                {userType === 'publisher' && isPublisherListeningMode && (
                  <MenuItem 
                    onClick={() => {
                      exitPublisherListeningMode();
                      safeNavigate(AppRoutes.PublisherDash);
                    }}
                  >
                    <MicExternalOnIcon/>
                    <Typography variant="body2" sx={textStyle}>
                      Publisher Dashboard
                    </Typography>
                  </MenuItem>
                )}

                {/* Listening-mode items for publishers */}
                {userType === 'publisher' && isPublisherListeningMode && (
                  <>
                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.PublisherProfile}>
                        <AccountCircleIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Public Profile
                        </Typography>
                    </MenuItem>

                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.ListenerPayments}>
                        <CreditCardIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Payment
                        </Typography>
                    </MenuItem>

                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.ListenerCurationSettings}>
                        <LibraryMusicIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Curation
                        </Typography>
                    </MenuItem>

                    <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.Messages}>
                        <MessageIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Messages
                        </Typography>
                    </MenuItem>
                  </>
                )}

                <MenuItem 
                    component={RouterLink}
                    to={AppRoutes.Support}>
                        <HelpOutlineIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Support
                        </Typography>
                    </MenuItem>

                {/* Playlists made while curating for a label are filed
                    under the label */}
                {userType !== 'label' && userType !== 'label_team_member' && (
                  curatingLabel ? (
                    <MenuItem onClick={() => { exitLabelListeningMode(); handleClose(); }}>
                        <HeadphonesIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Stop curating as {curatingLabel.name}
                        </Typography>
                    </MenuItem>
                  ) : curationLabels.map((label) => (
                    <MenuItem
                      key={`curate-${label.id}`}
                      onClick={() => {
                        enterLabelListeningMode({ id: label.id, name: label.name });
                        safeNavigate(AppRoutes.Home2);
                      }}
                    >
                        <HeadphonesIcon/>
                        <Typography variant="body2" sx={textStyle}>
                            Curate playlists as {label.name}
                        </Typography>
                    </MenuItem>
                  ))
                )}
                {/* Theme Selector */}
                <ThemeSelector variant="menuItem" onClose={handleClose} />

                <InstallAppMenuItem onClose={handleClose} textStyle={textStyle} />

                <MenuItem onClick={doLogout}>
                    <Logout/>
                    <Typography variant="body2" sx={textStyle}>
                        Logout
                    </Typography>
                </MenuItem>
            </Menu>
            
            {/* Unsaved Changes Dialog for Logout */}
            <Dialog
                open={showUnsavedChangesDialog}
                onClose={() => handleUnsavedChangesDialogClose('cancel')}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Typography variant="h6" component="div">
                        Unsaved Changes
                    </Typography>
                </DialogTitle>
                
                <DialogContent>
                    <Box sx={{ py: 2 }}>
                        <Typography variant="body1" gutterBottom>
                            You have unsaved changes to recording credits. If you log out now, these changes will be lost.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Please save your changes before logging out, or discard them to continue.
                        </Typography>
                    </Box>
                </DialogContent>
                
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button
                        onClick={() => handleUnsavedChangesDialogClose('discard')}
                        variant="outlined"
                        color="error"
                        disabled={isSavingChanges}
                    >
                        {pendingNavigation ? 'Discard & Continue' : 'Discard & Logout'}
                    </Button>
                    <Button
                        onClick={() => handleUnsavedChangesDialogClose('cancel')}
                        variant="text"
                        disabled={isSavingChanges}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => handleUnsavedChangesDialogClose('save')}
                        variant="contained"
                        color="primary"
                        disabled={isSavingChanges}
                        startIcon={isSavingChanges ? <CircularProgress size={16} /> : null}
                    >
                        {isSavingChanges ? 'Saving...' : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    )
}

export default ProfileMenu